/* global runtime */
// Lokale koblings- og bokføringshjelpere for banklinjer.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
// Speiler `functions/bank/bankReceiptPaymentSource.ts`. En bekreftet kobling er
// hardt bevis for hvor betalingen gikk fra, og skrives derfor paa bilaget i
// stedet for bare aa utledes i innboksen. Grensene er de samme: bare foretakets
// egne utskrifter, bare bankkontoer med hovedbokskonto, og aldri over et valg
// brukeren selv har tatt.
const bankLinkedReceiptAnalysisPatch = (state, statement, line, receipt) => {
  const analysis = receipt?.receiptAnalysis;
  if (!analysis || receipt.paymentSourceDecision) return null;
  if (['manual', 'manual-at-upload'].includes(String(analysis.paymentSourceOrigin || ''))) return null;
  if (statement?.source && statement.source !== 'business') return null;
  const account = [...safeArray(state?.settings?.bankAccounts), ...safeArray(state?.settings?.paymentAccounts)]
    .find((entry) => String(entry?.id || '') === String(statement?.bankAccountId || ''));
  const accountCode = String(account?.ledgerAccountCode || account?.ledger || account?.accountCode || '').trim();
  if (!/^19[2-9]\d$/.test(accountCode)) return null;
  const bankGross = Math.round(Math.abs(Number(line?.amount) || 0) * 100) / 100;
  const currentGross = Math.round(Number(analysis.totalGross) * 100) / 100;
  const net = Math.round(Number(analysis.documentNetAmount) * 100) / 100;
  const vat = Math.round(Number(analysis.documentVatAmount ?? analysis.vatAmount) * 100) / 100;
  const rounding = Math.round(Number(analysis.roundingAmount || 0) * 100) / 100;
  const canCorrectGross = bankGross > 0 && currentGross > 0 && Math.abs(bankGross - currentGross) > 0.01
    && String(analysis.currency || 'NOK').toUpperCase() === 'NOK'
    && net > 0 && vat >= 0 && Math.abs(Math.round((net + vat + rounding) * 100) / 100 - bankGross) <= 0.02;
  const purchaseLines = safeArray(analysis.purchaseLines);
  const adjustedLines = canCorrectGross && purchaseLines.length === 1
    ? [{ ...purchaseLines[0], amountGross: bankGross, amountNet: net, vatAmount: vat }]
    : purchaseLines;
  const riskFlags = safeArray(analysis.riskFlags)
    .filter((flag) => !['missing_payment_source', 'payment_evidence_review'].includes(String(flag?.code || '')));
  return {
    ...analysis,
    isPaid: true,
    ...(canCorrectGross ? {
      totalGross: bankGross,
      totalGrossNok: bankGross,
      purchaseLines: adjustedLines,
      purchaseLineValidation: null,
      bankAdjustedAmount: {
        fromTotalGross: currentGross,
        toTotalGross: bankGross,
        source: 'bank-match',
        statementId: String(statement?.id || ''),
        lineId: String(line?.id || ''),
      },
    } : {}),
    paymentStatusOrigin: 'bank-match',
    paymentAccountCode: accountCode,
    suggestedPaymentSource: 'company',
    paymentSourceOrigin: 'bank-match',
    paymentEvidence: {
      origin: 'bank-match',
      confidence: 'high',
      paymentSource: 'company',
      accountCode,
      matchedAmountNok: bankGross,
      matchedAmountRole: 'total_gross',
      requiresReview: false,
      reason: 'Bilaget er koblet til en banklinje på bedriftskontoen.',
      statementId: String(statement?.id || ''),
      lineId: String(line?.id || ''),
      linkedAt: nowIso(),
    },
    riskFlags,
    ...(riskFlags.length === 0 ? { receiptWorkflowStatus: 'quick_approval', approvalRoute: 'quick_approval' } : {}),
  };
};

// Naar koblingen fjernes, forsvinner betalingskilden den satte — og bare den.
const bankUnlinkedReceiptAnalysisPatch = (receipt, statementId, lineId) => {
  const analysis = receipt?.receiptAnalysis;
  const evidence = analysis?.paymentEvidence;
  if (!analysis || !evidence || evidence.origin !== 'bank-match') return null;
  if (String(evidence.statementId || '') !== String(statementId || '') || String(evidence.lineId || '') !== String(lineId || '')) return null;
  return { ...analysis, paymentAccountCode: null, suggestedPaymentSource: 'unknown', paymentSourceOrigin: null, paymentEvidence: null };
};

// ------------------------------------------------- papirkurven slipper ikke
//
// Et bilag som ligger i papirkurven skal bli liggende der naar koblingen til en
// banklinje loesnes. Uten dette gjorde «Koble fra» slettingen om til en
// gjenoppretting: bilaget kom tilbake i innboksen som `active`, og papirkurven
// sto tom rett etter at brukeren hadde faatt beskjed om at bilaget laa der.
//
// Resten av opprydningen — `bookedAt`, `bookedAs`, betalingskilden fra
// koblingen — gjelder likevel. Det er bare statusen papirkurven eier.
const bankReleasedReceiptStatus = (receipt) => receiptInboxEntryIsTrashed(receipt) ? receipt.status : 'active';

// Et bilag som legges i papirkurven skal ikke bli haengende paa en banklinje.
// Slettingen skrev bare bilagets egen status, og linjen ble staaende med
// koblingen: banksiden viste et bilag som laa i papirkurven, og «Koble fra»
// hentet det ut igjen. `bankStatements` eies av backend, saa koblingen loesnes
// gjennom avstemmingsmutasjonen foer bilaget legges i papirkurven. Feiler
// frakoblingen — en avstemt utskrift — feiler slettingen med den samme
// forklaringen i stedet for aa etterlate en halv kobling.
//
// Rene forslag roeres ikke: den kontinuerlige matchingen ser bort fra bilag i
// papirkurven og rydder dem bort av seg selv.
const releaseBankLinesForTrashedReceipts = async (entryIds = []) => {
  const ids = new Set(safeArray(entryIds));
  const linked = bankLinkedReceiptIndex(mergeState(runtime.appState));
  const lines = new Map();
  ids.forEach((id) => {
    const link = linked.get(String(id));
    if (link && !link.suggested) lines.set(`${link.statementId}|${link.lineId}`, link);
  });
  for (const link of lines.values()) await detachReceiptFromBankLine(link.statementId, link.lineId);
  return { ids, undocumentedLines: bankLinesLeftWithoutReceipt([...lines.values()]) };
};

// Et bilag kan fjernes fra en linje uten aa aapne avstemmingen — det er
// dokumentasjon, ikke en foering. Men blir linjen staaende uten bilag, er det
// fortsatt en mangel som skal registreres med begrunnelse, ikke bli borte i
// stillhet. Linjene rapporteres derfor tilbake til den som slettet bilaget.
const bankLinesLeftWithoutReceipt = (links = []) => {
  const state = mergeState(runtime.appState);
  return safeArray(links).map((link) => {
    const statement = safeArray(state.bankStatements).find((entry) => entry.id === link.statementId);
    const line = safeArray(statement?.lines).find((entry) => entry.id === link.lineId);
    // En linje som er matchet mot en bokfoert post er ikke udokumentert: bilaget
    // ligger paa posten. Det er nettopp den linjen dobbeltopplastingen henger paa.
    if (!line || line.matchedEntryId || !bankLineNeedsReceipt(line)) return null;
    return { statementId: link.statementId, id: line.id, date: isoToNb(line.date) || line.date || '', title: line.description || 'Banklinje', amount: num(line.amount) };
  }).filter(Boolean);
};

const bankLineReceiptRefsLocal = (line = {}) => {
  const refs = [];
  safeArray(line.receiptAttachments).forEach((entry) => {
    const receiptId = String(entry?.receiptInboxEntryId || entry?.id || '').trim();
    if (!receiptId) return;
    refs.push(stripUndefinedDeep({
      receiptInboxEntryId: receiptId,
      attachmentName: entry.attachmentName || entry.name || 'Bilag',
      attachmentId: entry.attachmentId || '',
      attachmentMimeType: entry.attachmentMimeType || '',
      attachmentDownloadUrl: entry.attachmentDownloadUrl || entry.downloadUrl || entry.file || '',
      documentRole: entry.documentRole || '',
    }));
  });
  const legacyId = String(line.receiptInboxEntryId || line.aiSuggestedReceiptInboxEntryId || '').trim();
  if (legacyId && !refs.some((entry) => entry.receiptInboxEntryId === legacyId)) refs.unshift(stripUndefinedDeep({
    receiptInboxEntryId: legacyId,
    attachmentName: line.attachmentName || 'Bilag',
    attachmentId: line.attachmentId || '',
    attachmentMimeType: line.attachmentMimeType || '',
    attachmentDownloadUrl: line.attachmentDownloadUrl || '',
  }));
  return refs;
};

const bankReceiptRefForInboxEntryLocal = (receipt) => {
  const attachment = safeArray(receipt?.attachments)[0];
  const analysis = receipt?.receiptAnalysis || {};
  const supportingPaymentConfirmation = analysis.documentKind === 'payment_confirmation' || analysis.documentType === 'payment_confirmation';
  return stripUndefinedDeep({
    receiptInboxEntryId: String(receipt?.id || '').trim(),
    attachmentName: receipt?.attachmentName || receipt?.name || 'Bilag',
    attachmentId: receipt?.attachmentId || attachment?.id || receipt?.id,
    attachmentMimeType: receipt?.attachmentMimeType || attachment?.mimeType,
    attachmentDownloadUrl: attachment?.downloadUrl || attachment?.file || receipt?.downloadUrl || receipt?.file,
    documentRole: supportingPaymentConfirmation ? 'payment_confirmation' : 'primary',
  });
};

const attachReceiptToBankLineLocal = (statementId, lineId, receiptId) => {
  const receipt = safeArray(runtime.appState?.receiptInbox).find((entry) => entry.id === receiptId);
  if (!receipt) return;
  const attachStatement = safeArray(runtime.appState?.bankStatements).find((entry) => entry.id === statementId);
  const attachLine = safeArray(attachStatement?.lines).find((entry) => entry.id === lineId);
  const linkedAnalysis = bankLinkedReceiptAnalysisPatch(runtime.appState, attachStatement, attachLine, receipt);
  const nextReceiptRef = bankReceiptRefForInboxEntryLocal(receipt);
  const supportingPaymentConfirmation = nextReceiptRef.documentRole === 'payment_confirmation';
  updateState((state) => ({
    ...state,
    bankStatements: safeArray(state.bankStatements).map((statement) =>
      statement.id === statementId
        ? {
            ...statement,
            lines: safeArray(statement.lines).map((line) => {
              if (line.id !== lineId) return line;
              const receiptRefs = bankLineReceiptRefsLocal(line);
              const hasPrimaryReceipt = receiptRefs.some((entry) => entry.documentRole !== 'payment_confirmation');
              const nextReceiptRefsBase = receiptRefs.some((entry) => entry.receiptInboxEntryId === receipt.id)
                ? receiptRefs.map((entry) => entry.receiptInboxEntryId === receipt.id ? nextReceiptRef : entry)
                : [...receiptRefs, nextReceiptRef];
              const nextReceiptRefs = !supportingPaymentConfirmation && !hasPrimaryReceipt
                ? [nextReceiptRef, ...nextReceiptRefsBase.filter((entry) => entry.receiptInboxEntryId !== receipt.id)]
                : nextReceiptRefsBase;
              if (supportingPaymentConfirmation) return { ...line, receiptAttachments: nextReceiptRefs, supportingDocumentMatchedAt: nowIso() };
              const primaryReceipt = nextReceiptRefs[0] || nextReceiptRef;
              const nextLine = { ...line };
              delete nextLine.receiptWaived;
              delete nextLine.receiptWaiverReason;
              delete nextLine.receiptWaivedAt;
              delete nextLine.receiptWaivedByUserId;
              delete nextLine.documentationReason;
              return {
                ...nextLine,
                attachmentName: primaryReceipt.attachmentName || 'Bilag',
                attachmentId: primaryReceipt.attachmentId || primaryReceipt.receiptInboxEntryId,
                attachmentMimeType: primaryReceipt.attachmentMimeType,
                attachmentDownloadUrl: primaryReceipt.attachmentDownloadUrl,
                receiptInboxEntryId: primaryReceipt.receiptInboxEntryId,
                receiptAttachments: nextReceiptRefs,
                documentationMode: 'receipt',
              };
            }),
          }
        : statement,
    ),
    receiptInbox: safeArray(state.receiptInbox).map((entry) => {
      if (entry.id === receiptId) return {
        ...entry,
        status: supportingPaymentConfirmation ? 'linked-supporting-document' : 'linked-bank-line',
        bookedAt: undefined,
        bookedAs: supportingPaymentConfirmation ? 'supporting-document' : 'bank-line',
        linkedEntryId: undefined,
        linkedBankLineAt: nowIso(),
        ...(linkedAnalysis ? { receiptAnalysis: linkedAnalysis } : {}),
      };
      return entry;
    }),
  }), true);
};
const attachReceiptToBankLine = async (statementId, lineId, receiptId) => {
  try {
    const result = await bankReconciliationMutation('attach_receipt', { statementId, lineId, receiptId });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    attachReceiptToBankLineLocal(statementId, lineId, receiptId);
    return null;
  }
};

const bankLineTrace = (statement, line) => stripUndefinedDeep({
  statementId: statement.id,
  statementFileName: statement.fileName || statement.originalFileName,
  statementSource: statement.source || 'business',
  statementBankAccountId: statement.bankAccountId,
  statementPeriodFrom: statement.periodFrom,
  statementPeriodTo: statement.periodTo,
  lineId: line.id,
  lineDate: line.date,
  accountingDate: line.accountingDate,
  lineDescription: line.description,
  lineAmount: num(line.amount),
});

const bankLineReceipt = (line) => stripUndefinedDeep({
  id: line.receiptInboxEntryId,
  attachmentId: line.attachmentId,
  attachmentName: line.attachmentName,
  attachmentMimeType: line.attachmentMimeType,
  downloadUrl: line.attachmentDownloadUrl,
  name: line.attachmentName,
});

const bankVatCodeFromRate = (rate) => {
  const normalized = num(rate);
  if (normalized === 25) return '25';
  if (normalized === 15) return '15';
  if (normalized === 12) return '12';
  return '';
};

const bankLineBookedPatch = (entryType, entryId, note) => stripUndefinedDeep({
  classification: 'business',
  matchedEntryType: entryType,
  matchedEntryId: entryId,
  reconciliationStatus: 'matched',
  note,
  aiSuggestedClassification: undefined,
  aiSuggestedBookingType: undefined,
  aiSuggestedAccountCode: undefined,
  aiSuggestedVatRate: undefined,
  aiSuggestedReverseChargeCode: undefined,
  aiSuggestedReason: undefined,
  aiSuggestionConfidence: undefined,
});

const updateBankLineAfterBooking = async (statementId, lineId, patch) => {
  const markReceiptBookedLocal = () => updateState((state) => {
    const statement = safeArray(state.bankStatements).find((entry) => entry.id === statementId);
    const line = safeArray(statement?.lines).find((entry) => entry.id === lineId);
    const receiptId = line?.receiptInboxEntryId || line?.aiSuggestedReceiptInboxEntryId || patch?.receiptInboxEntryId;
    if (!receiptId) return state;
    const bookedAs = patch?.matchedEntryType === 'other-sale' ? 'sale' : patch?.matchedEntryType || 'bank-line';
    return { ...state, receiptInbox: safeArray(state.receiptInbox).map((entry) => entry.id === receiptId ? stripUndefinedDeep({ ...entry, status: 'booked', bookedAt: nowIso(), bookedAs, linkedEntryId: patch?.matchedEntryId || entry.linkedEntryId }) : entry) };
  }, true);
  try {
    const result = await bankReconciliationMutation('mark_line_booked', { statementId, lineId, patch });
    return result.statement ? mapBankStatementToUi(result.statement) : null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    markReceiptBookedLocal();
    return updateBankStatementLineLocal(statementId, lineId, patch);
  }
};

const bankStatementLedgerAccountCode = (state, statement, fallback = '1920') => {
  const account = safeArray(state?.settings?.bankAccounts).find((entry) => entry?.id === statement?.bankAccountId);
  return getLedgerAccountCode(account, state?.settings?.bankAccountingAccount || fallback);
};

const parseBankTermYear = (value, fallback) => {
  const match = String(value || '').match(/\b(20\d{2})\b/);
  return match ? Number(match[1]) : Number(fallback || runtime.appState?.activeYear || new Date().getFullYear());
};
const payrollTaxBankBookingRequiresPayrollFlow = (state, line = {}) =>
  parseBankTermYear(line.taxTerm, state?.activeYear) >= 2026;

const resolveBankTaxPaymentAccountCode = (state, line = {}) => {
  const fiscalYear = parseBankTermYear(line.taxTerm, state?.activeYear);
  const scope = resolveAccountingScope(state?.settings || {}, fiscalYear);
  if (scope.jurisdiction !== 'NO') {
    throw new Error(t('r1e.bookingHelpers.taxPaymentNoOnly'));
  }
  if (scope.entityFamily === 'limited_company') {
    const paymentKind = String(line.taxPaymentKind || line.taxPaymentType || '').trim().toLowerCase();
    if (['advance', 'advance_tax', 'additional_advance', 'additional_advance_tax'].includes(paymentKind)) return '2540';
    return '2510';
  }
  if (scope.entityFamily === 'sole_trader') return '2060';
  throw new Error(t('r1e.bookingHelpers.taxPaymentManualReview'));
};

const parseVatTermKey = (value) => {
  const text = String(value || '').toLowerCase();
  const match = text.match(/(\d+)\s*\.\s*termin/);
  if (match) return `term${match[1]}`;
  if (text.includes('10. juni') || text.includes('10 juni')) return 'term1';
  if (text.includes('31. august') || text.includes('31 august')) return 'term2';
  if (text.includes('10. oktober') || text.includes('10 oktober')) return 'term3';
  if (text.includes('10. desember') || text.includes('10 desember')) return 'term4';
  if (text.includes('10. februar') || text.includes('10 februar')) return 'term5';
  if (text.includes('10. april') || text.includes('10 april')) return 'term6';
  return 'term1';
};
